import { useLayoutEffect, useRef, useState } from "react";
import { polygonBottom, polygonCrossingsY, polygonTop } from "../util/polygon";


interface SvgPolygonWrappableTextProps {
  text: string;
  polygon: number[]; // alternating x and y coordinates
  color: string;
  fontFamily?: string;
  fontSize: number;
  lineHeight?: number;
  textAnchor: string;
  onLayout: (height: number, boxes: SVGRect[], overflow: boolean) => void;
}

export default function SvgPolygonWrappableText(props: SvgPolygonWrappableTextProps) {
  const { text, polygon, color, fontFamily = 'Montserrat', fontSize, lineHeight = 1.3, textAnchor, onLayout } = props;
  const unwrappedTextRef = useRef<SVGTextElement>(null);
  const wrappedTextRef = useRef<SVGTextElement>(null);
  const [textLines, setTextLines] = useState<string[]>([]);
  const [boldLineCount, setBoldLineCount] = useState(1);
  const [xs, setXs] = useState<number[]>([]);
  const tspanRefs = useRef<Map<number, SVGTSpanElement | null>>(new Map());
  const [baselineOffset, setBaselineOffset] = useState(0);
  const [debugWrapLines, setDebugWrapLines] = useState([]);

  const headerY = polygonTop(polygon) + baselineOffset;
  const width = 500;

  useLayoutEffect(() => {
    if (!unwrappedTextRef || !unwrappedTextRef.current || text.length === 0) {
      return;
    }
    const bbox = unwrappedTextRef.current.getBBox();
    const currentBaseline = unwrappedTextRef.current.y.baseVal.getItem(0).value;
    const newBaselineOffset = currentBaseline - bbox.y;
    const textNewlinesAsWords = text.replace(/\n/g, " \n ");
    const removeDoubleSpace = textNewlinesAsWords.replace(/  /g, " ");
    const words = removeDoubleSpace.split(' ');
    const lines: string[] = [];
    const newXs: number[] = [];
    const newDebugWrapLines = [];
    var newBoldLineCount = 0;
    let currentLine = '';
    let startIndex = 0;

    var y = polygonTop(polygon) + newBaselineOffset;
    var x = 0;
    var availableWidth = 0;

    const measureNewLineBox = () => {
      var [ x0, x1 ] = polygonCrossingsY(polygon, y);
      newDebugWrapLines.push([ x0, x1, y]);
      var [ x0high, x1high ] = polygonCrossingsY(polygon, y - 0.6 * fontSize);
      newDebugWrapLines.push([ x0high, x1high, y - 0.6 * fontSize])
      if (x0 !== undefined && x0high !== undefined) {
        x = Math.max(x0, x0high);
        availableWidth = Math.min(x1, x1high) - x;
      }
      newXs.push(x);
      y += fontSize * lineHeight;
    }
    measureNewLineBox();

    const unwrappedTextElement = unwrappedTextRef.current;
    const unwrappedCharCount = unwrappedTextElement.getNumberOfChars();
    const safeWidth = (startIndex, charCount) => unwrappedTextElement.getSubStringLength(
      Math.min(startIndex, unwrappedCharCount - 1),
      Math.min(charCount, unwrappedCharCount - startIndex));

    words.forEach((word, index) => {
      if (word === '\n') {
        lines.push(currentLine);
        if (newBoldLineCount === 0) {
          newBoldLineCount = lines.length;
        }
        measureNewLineBox();
        startIndex += currentLine.length + 1;
        currentLine = '';  
        return;
      }

      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = safeWidth(startIndex, testLine.length);
  
      if (testWidth > availableWidth && index > 0) {
        const breakIndex = word.indexOf('​');
        if (breakIndex > 0) {
          const preBreak = word.substring(0, breakIndex);
          const postBreak = word.substring(breakIndex + 1);
          const testLineBreakWord = currentLine ? `${currentLine} ${preBreak}-` : (preBreak + '-');
          const testWidthBreakWord = safeWidth(startIndex, testLineBreakWord.length) + fontSize * 0.5;
          if (testWidthBreakWord <= availableWidth) {
            lines.push(testLineBreakWord);
            measureNewLineBox();
            startIndex += testLineBreakWord.length;
            currentLine = postBreak;
          } else {
            lines.push(currentLine);
            measureNewLineBox();
            startIndex += currentLine.length + 1;
            currentLine = word;  
          }
        } else {
          lines.push(currentLine);
          measureNewLineBox();
          startIndex += currentLine.length + 1;
          currentLine = word;
        }
      } else {
        currentLine = testLine;
      }
    });
    lines.push(currentLine);

    setBaselineOffset(currentBaseline - bbox.y)
    setTextLines(lines);
    setXs(newXs);
    setDebugWrapLines(newDebugWrapLines);
    setBoldLineCount(newBoldLineCount);
  }, [text, fontFamily, fontSize, lineHeight, width]);

  useLayoutEffect(() => {
    if (!wrappedTextRef || !wrappedTextRef.current) {
      return;
    }
    const wbbox = wrappedTextRef.current.getBBox()
    const boxes = textLines.map((_, index) => {
      const ref = tspanRefs.current.get(index)
      return ref ? ref.getBBox() : new DOMRect(0, 0, 0, 0);
    })
    const availableHeight = polygonBottom(polygon) - polygonTop(polygon);
    onLayout(wbbox.height, boxes, wbbox.height > availableHeight);
  }, [textLines])

  const setRef = (index: number) => (el: SVGTSpanElement | null) => {
    if (el) {
      tspanRefs.current.set(index, el);
    } else {
      tspanRefs.current.delete(index);
    }
  };

  const coords: string[] = [];
  for (let i = 0; i < polygon.length; i += 2) {
    coords.push(`${polygon[i].toFixed(1)} ${polygon[i + 1].toFixed(1)}`)
  }
  const d = "M" + coords.join(" L") + "Z";


  return (
    <g>
      {false && debugWrapLines.map((l, i) => {
        return (
          <line 
            key={"l" + i}
            x1={l[0]}
            y1={l[2]}
            x2={l[1]}
            y2={l[2]}
            stroke="pink"
            strokeWidth={5}
          />
        )
      })}
      {false && <path 
        fill="none"
        stroke="purple"
        strokeWidth={5}
        d={d}
      />}      
      {/* For wrapped text there's a wrapped and an unwrapped version,
          The unwrapped version allows recalculation of the wrapping
          without toggling to a one-liner before recalculation.  */}
      <text ref={unwrappedTextRef} 
        x={0} y={headerY} 
        fontSize={fontSize} 
        fill={`rgba(255, 255, 255, 0.0)`}
        fontFamily={fontFamily}
        fontWeight="400"
        textAnchor={textAnchor}
      >
         {text}
      </text>
      {textLines.length > 0 && <text ref={wrappedTextRef}
        x={0} y={headerY} 
        fontSize={fontSize} 
        fill={color}
        fontFamily={fontFamily}
        fontWeight="400"
        textAnchor={textAnchor}
      >
        {textLines.map((line, index) => (
          <tspan key={index} x={xs[index]} dy={index ? `${fontSize * lineHeight}` : 0} ref={setRef(index)} fontWeight={index < boldLineCount ? "600" : "400"}>
            {line || " "}
          </tspan>
        ))}
      </text>}
    </g>
  )

}